import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./index.module.scss";
import News from "/components/side/news";
import RightSide from "/components/layouts/rightSide";
import TextWithGallary from "../../../common/textWithGallary";
import Button from "/components/common/button";
import TrailerMassCalculator from "./trailerMassCalculator";
import { showPopup } from "/redux/action-creaters/popup";
import { popupTypes } from "/redux/reducers/popupReducer";

function generateAutoTemplate(news, internalLinks = []) {
  // У части кейсов установки товар не привязан — в панели заполнена только
  // электрика или одно описание. Без запасного пустого объекта обращение
  // к product.DETAIL_PAGE_URL роняло страницу с кодом 500 (ТЗ п. 6.6)
  const product = news.PRODUCT || {};
  const electrica = news.ELECTRICA;
  const props = news.PROPS || {};

  // Проверяем, есть ли электрика (если есть хотя бы одно поле с данными)
  const hasElectrica = Boolean(electrica);

  // Формируем ссылку на товар
  const productLink = product.DETAIL_PAGE_URL
    ? `<a href="${product.DETAIL_PAGE_URL}">${product.NAME || "фаркоп"}</a>`
    : "фаркоп";

  // Формируем ссылку на электрику (только если электрика есть)
  const electricaLink =
    // eslint-disable-next-line no-nested-ternary
    hasElectrica && electrica.DETAIL_PAGE_URL
      ? `<a href="${electrica.DETAIL_PAGE_URL}">${electrica.NAME || "комплект электрики"}</a>`
      : hasElectrica
        ? "комплект электрики"
        : null;

  // Формируем основной текст установки
  const installationText = hasElectrica
    ? `Установили ${productLink} и ${electricaLink}`
    : `Установили ${productLink}`;

  // Генерируем характеристики фаркопа
  const farkopCharacteristics = [];
  if (product.TYAGOVAYA_NAGRUZKA_KG) {
    farkopCharacteristics.push(
      `<li>Тяговая нагрузка: ${product.TYAGOVAYA_NAGRUZKA_KG} кг.</li>`,
    );
  }
  if (product.VERTIKALNAYA_NAGRUZKA) {
    farkopCharacteristics.push(
      `<li>Вертикальная нагрузка: ${product.VERTIKALNAYA_NAGRUZKA} кг.</li>`,
    );
  }
  if (product.VYREZ_BAMPERA) {
    farkopCharacteristics.push(
      `<li>Вырез бампера: ${product.VYREZ_BAMPERA}.</li>`,
    );
  }
  if (product.TIP_KRYUKA) {
    farkopCharacteristics.push(`<li>Тип крюка: ${product.TIP_KRYUKA}.</li>`);
  }
  if (product.PASPORT_I_SERTIFIKAT) {
    farkopCharacteristics.push(
      `<li>Паспорт и сертификат: ${product.PASPORT_I_SERTIFIKAT}.</li>`,
    );
  }

  // Генерируем характеристики электрики (только если электрика есть)
  const electricaCharacteristics = [];
  if (hasElectrica) {
    if (electrica.KOL_VO_KONTAKTOV) {
      electricaCharacteristics.push(
        `<li>Кол-во контактов: ${electrica.KOL_VO_KONTAKTOV}.</li>`,
      );
    }
    if (electrica.PROPERTY_OTKLYUCHENIE_SHTATNYKH_PARKTRONIKOV) {
      electricaCharacteristics.push(
        `<li>Штатные парктроники: ${electrica.PROPERTY_OTKLYUCHENIE_SHTATNYKH_PARKTRONIKOV}.</li>`,
      );
    }
    if (electrica.PROPERTY_TREBUETSYA_AKTIVATSIYA) {
      electricaCharacteristics.push(
        `<li>Активация электрики: ${electrica.PROPERTY_TREBUETSYA_AKTIVATSIYA}.</li>`,
      );
    }
  }

  // Время выполнения работ
  const workTime = props.WORK_TIME || "2 часа 30 мин";

  return `
    <p>${installationText}</p>
    
    <h3>Характеристики фаркопа:</h3>
    <ul>
      ${farkopCharacteristics.join("")}
    </ul>
    
    ${
      hasElectrica
        ? `
    <h3>Характеристики электрики:</h3>
    <ul>
      ${electricaCharacteristics.join("")}
    </ul>
    `
        : ""
    }
    
    <h3>Результаты установки фаркопа:</h3>
    <ul>
      <li>Время выполнения работ: ${workTime}.</li>
      <li>Гарантия на фаркоп: 1 год.</li>
      <li>Гарантия на работы: 3 года.</li>
      <li>Выданы документы: паспорт/инструкция на фаркоп, сертификат на фаркоп, сертификат установочного центра, акт выполненных работ.</li>
    </ul>

    ${
      internalLinks.length > 0
        ? `<p>Вы также можете подобрать в каталоге другие ${internalLinks
            .map((link) => `<a href="${link.url}">${link.anchor}</a>`)
            .join(", ")}.</p>`
        : ""
    }
  `;
}

export default function Detail({
  hideDate = false,
  code,
  isFarkop,
  news,
  isShowList = false,
}) {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (isShowList) {
      // eslint-disable-next-line no-use-before-define
      setItems(extractHeadings(news.DETAIL_TEXT));
    }
  }, []);

  const { contacts } = useSelector((state) => state.contacts);
  const { internalLinks } = useSelector((state) => state.meta);

  return (
    <RightSide
      title={news.NAME}
      side={
        isFarkop && (
          <div className={styles.Side}>
            {!isShowList && <News />}
            {isShowList && items.length > 0 && (
              <div className={styles.Block}>
                <div className={styles.BlockTitle}>Содержание</div>
                <ul>
                  {items.map((item) => (
                    <li
                      key={item.text}
                      className={styles[`Block${item.type}`]}
                      onClick={() => {
                        const headings = document.querySelectorAll("h2,h3");
                        headings.forEach((element) => {
                          if (element.innerText === item.text) {
                            element.scrollIntoView({ behavior: "smooth" });
                            const yOffset = -100;
                            const y =
                              element.getBoundingClientRect().top +
                              window.pageYOffset +
                              yOffset;
                            window.scrollTo({ top: y, behavior: "smooth" });
                          }
                        });
                      }}
                    >
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className={styles.Drive}>
              <span>Следите за новостями: </span>
              <div>
                <a
                  className={styles.Drive2}
                  href="https://www.drive2.ru/users/farkopru/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src="/drive2.svg" />
                </a>
                <a
                  className={styles.Drive2}
                  href="https://vk.com/farkopru"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src="/vk.svg" />
                </a>
              </div>
            </div>
          </div>
        )
      }
    >
      <div>
        {!hideDate && <div className={styles.Date}>{news.FORMATTED_DATE}</div>}
        {news.PICTURE_SRC && (
          <img
            className={styles.Image}
            src={news.PICTURE_SRC}
            alt={news.NAME}
            title={news.NAME}
          />
        )}
        {news.PROPS?.AUTO ? (
          <div
            className="text"
            dangerouslySetInnerHTML={{
              __html: generateAutoTemplate(news, internalLinks),
            }}
          />
        ) : (
          <TextWithGallary
            text={news.DETAIL_TEXT?.replaceAll(
              "#PHONE#",
              contacts?.phone,
            ).replaceAll("#CALL_TOUCH_CODE#", contacts?.calltouchCode)}
          />
        )}
        {code === "kategoria-prav-s-pricepom" && <TrailerMassCalculator />}
        {news.PRODUCT?.ID && (
          <div style={{ marginTop: "30px", textAlign: "center" }}>
            <Button
              type=""
              label="Хочу так же"
              onClick={() => {
                dispatch(
                  showPopup(popupTypes.buyOneClick, {
                    id: news.PRODUCT.ID,
                  }),
                );
              }}
            />
          </div>
        )}
      </div>
    </RightSide>
  );
}

function extractHeadings(htmlText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlText, "text/html");
  const headingsElements = doc.querySelectorAll("h2,h3");
  const headings = Array.from(headingsElements).map((element) => {
    return {
      type: element.tagName,
      text: element.textContent.trim(),
    };
  });
  return headings;
}
